import { useRef } from "react";
import { Btn } from "../../../components/ui/Button";
import { Chip } from "../../../components/ui/Chip";
import {
  canSave,
  pluralRows,
  ROW_STATUS_DISPLAY,
  ROW_STATUS_ORDER,
  rowCount,
  saveButtonLabel,
  saveSummary,
} from "./rowStatus";
import type { NomenclatureUpload, RowStatus, UploadRow } from "../types";
import type { HeldTdsFile, HeldTdsState } from "./useHeldTdsFiles";

const ROW_STATUSES_THAT_GET_A_GRADE = new Set<RowStatus>(["Ready", "Waiting", "Existing", "Saved"]);

const HELD_TDS_LABELS: Record<Exclude<HeldTdsState, "held">, [ChipVariant, string]> = {
  attaching: ["default", "Attaching…"],
  attached: ["ok", "✓ Attached"],
  alreadyAttached: ["ok", "Already on this grade"],
  failed: ["bad", "Failed"],
};

type ChipVariant = "default" | "ok" | "bad" | "info";

export interface TdsActions {
  heldFiles: HeldTdsFile[];
  addFiles: (uploadRowId: number, files: File[]) => void;
  removeFile: (heldFileId: number) => void;
  retryFile: (heldFileId: number) => void;
}

interface Props {
  upload: NomenclatureUpload;
  canWrite: boolean;
  isBusy: boolean;
  tdsActions: TdsActions;
  onSave: () => void;
  onDiscard: () => void;
}

export function UploadPreview({ upload, canWrite, isBusy, tdsActions, onSave, onDiscard }: Props) {
  const statusSummary = ROW_STATUS_ORDER.filter((status) => rowCount(upload, status) > 0);
  const canAddTds = canWrite && upload.uploadStatus !== "Discarded";

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-wrap gap-2">
        {statusSummary.map((status) => (
          <Chip key={status} variant={ROW_STATUS_DISPLAY[status].chipVariant}>
            {pluralRows(rowCount(upload, status))} · {ROW_STATUS_DISPLAY[status].label}
          </Chip>
        ))}
      </div>

      <div className="overflow-auto max-h-[520px] border border-rs-jet rounded-md">
        <table className="w-full border-collapse text-[13px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-rs-black text-left text-rs-light-gray text-[11.5px] uppercase tracking-[0.05em]">
              <th className="font-medium px-3 py-2.5">Row</th>
              <th className="font-medium px-3 py-2.5">Grade</th>
              <th className="font-medium px-3 py-2.5">Designation</th>
              <th className="font-medium px-3 py-2.5">Reads As</th>
              <th className="font-medium px-3 py-2.5">Resin family</th>
              <th className="font-medium px-3 py-2.5">Result</th>
              <th className="font-medium px-3 py-2.5">TDS</th>
            </tr>
          </thead>
          <tbody>
            {upload.rows.map((uploadRow) => (
              <UploadPreviewRow
                key={uploadRow.uploadRowId}
                uploadRow={uploadRow}
                canAddTds={canAddTds && ROW_STATUSES_THAT_GET_A_GRADE.has(uploadRow.rowStatus)}
                tdsActions={tdsActions}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <p className="m-0 flex-1 min-w-[220px] text-rs-light-gray text-[13px]">{saveSummary(upload)}</p>
        {upload.uploadStatus === "Review" && (
          <>
            <Btn variant="ghost" onClick={onDiscard} disabled={!canWrite || isBusy}>
              Discard Upload
            </Btn>
            <Btn onClick={onSave} disabled={!canWrite || isBusy || !canSave(upload)}>
              <span className="material-symbols-sharp">save</span>
              {saveButtonLabel(upload)}
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}

interface UploadPreviewRowProps {
  uploadRow: UploadRow;
  canAddTds: boolean;
  tdsActions: TdsActions;
}

function UploadPreviewRow({ uploadRow, canAddTds, tdsActions }: UploadPreviewRowProps) {
  const statusDisplay = ROW_STATUS_DISPLAY[uploadRow.rowStatus];
  const extraCodes = [
    uploadRow.supplierCode && `Supplier code ${uploadRow.supplierCode}`,
    uploadRow.tradenameCode && `Tradename code ${uploadRow.tradenameCode}`,
    uploadRow.hardnessColumnValue && `Hardness ${uploadRow.hardnessColumnValue}`,
  ].filter(Boolean);
  const storedDifferently =
    uploadRow.storedDesignationCode &&
    uploadRow.storedDesignationCode !== uploadRow.designationAsUploaded &&
    uploadRow.rowStatus !== "Error";

  return (
    <tr className="border-t border-rs-jet align-top">
      <td className="px-3 py-2.5 text-rs-light-gray">{uploadRow.spreadsheetRowNumber}</td>
      <td className="px-3 py-2.5">
        <span className="text-rs-off-white">{uploadRow.tradename || "—"}</span>
        <span className="block text-rs-light-gray text-xs mt-0.5">
          {uploadRow.supplier || "—"}
          {uploadRow.processingApplication ? ` · ${uploadRow.processingApplication}` : ""}
        </span>
        {extraCodes.length > 0 && (
          <span className="block text-rs-light-gray text-xs mt-0.5">{extraCodes.join(" · ")}</span>
        )}
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <code className="text-[12.5px]">{uploadRow.designationAsUploaded || "—"}</code>
        {storedDifferently && (
          <>
            <span className="block text-rs-light-gray text-[11.5px] mt-1">stored as</span>
            <code className="block text-[12.5px] text-rs-green">{uploadRow.storedDesignationCode}</code>
          </>
        )}
      </td>
      <td className="px-3 py-2.5 text-rs-off-white">{uploadRow.readsAs || "—"}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        {uploadRow.resinFamilyCode ? (
          <>
            <span className="text-rs-off-white">{uploadRow.resinFamilyCode}</span>
            {uploadRow.resinFamilyIsSuggested && (
              <span className="block text-rs-light-gray text-xs mt-0.5">suggested</span>
            )}
          </>
        ) : (
          <span className="text-rs-light-gray">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 min-w-[220px]">
        <Chip variant={statusDisplay.chipVariant}>{statusDisplay.label}</Chip>
        {uploadRow.problems.map((problem) => (
          <span key={problem} className="block text-rs-light-gray text-xs mt-1.5 max-w-[36ch]">
            {problem}
          </span>
        ))}
        {uploadRow.codesWaitingForApproval.length > 0 && (
          <span className="block text-rs-light-gray text-xs mt-1.5 max-w-[36ch]">
            Waiting on {uploadRow.codesWaitingForApproval.map((code) => code.code).join(", ")}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 min-w-[200px]">
        <RowTdsFiles uploadRow={uploadRow} canAddTds={canAddTds} tdsActions={tdsActions} />
      </td>
    </tr>
  );
}

function RowTdsFiles({ uploadRow, canAddTds, tdsActions }: UploadPreviewRowProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rowFiles = tdsActions.heldFiles.filter((heldFile) => heldFile.uploadRowId === uploadRow.uploadRowId);
  const heldLabel =
    uploadRow.rowStatus === "Waiting" ? "Attaches when its codes are approved" : "Attaches on Save";
  const linkButtonClasses = "bg-transparent border-0 p-0 text-xs text-rs-green cursor-pointer hover:underline";

  if (!canAddTds && !rowFiles.length) return <span className="text-rs-light-gray">—</span>;

  return (
    <div className="flex flex-col gap-1.5 items-start">
      {rowFiles.map((heldFile) => {
        const [chipVariant, chipLabel] =
          heldFile.state === "held" ? (["info", heldLabel] as const) : HELD_TDS_LABELS[heldFile.state];
        return (
          <div key={heldFile.heldFileId} className="flex flex-col gap-0.5 max-w-[30ch]">
            <span className="text-rs-off-white text-xs break-all">{heldFile.file.name}</span>
            <span className="flex gap-2 items-center flex-wrap">
              <Chip variant={chipVariant}>{chipLabel}</Chip>
              {heldFile.state === "held" && (
                <button
                  type="button"
                  className={linkButtonClasses}
                  aria-label={`Remove ${heldFile.file.name}`}
                  onClick={() => tdsActions.removeFile(heldFile.heldFileId)}
                >
                  ✕
                </button>
              )}
              {heldFile.state === "failed" && (
                <button type="button" className={linkButtonClasses} onClick={() => tdsActions.retryFile(heldFile.heldFileId)}>
                  Retry
                </button>
              )}
            </span>
            {heldFile.failureText && <span className="text-rs-light-gray text-xs">{heldFile.failureText}</span>}
          </div>
        );
      })}
      {canAddTds && (
        <>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 bg-transparent border border-rs-jet-light rounded px-2.5 py-1 text-xs text-rs-off-white cursor-pointer hover:border-rs-green hover:text-rs-green"
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="material-symbols-sharp text-base! align-middle!" aria-hidden="true">
              upload_file
            </span>
            Add TDS
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            multiple
            className="hidden"
            aria-label={`TDS files for ${uploadRow.tradename}`}
            onChange={(event) => {
              const pdfFiles = [...(event.target.files ?? [])].filter((file) => /\.pdf$/i.test(file.name));
              if (pdfFiles.length) tdsActions.addFiles(uploadRow.uploadRowId, pdfFiles);
              event.target.value = "";
            }}
          />
        </>
      )}
    </div>
  );
}
